import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Plus, Edit2, Trash2, Save, X, Layers, Dumbbell, 
  ChevronDown, ChevronUp, Search, GripVertical, Clock
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import axios from 'axios';
import { API } from '../utils/api';
import { toast } from 'sonner';

export const SuperSeriesManager = () => {
  // Estados para Métodos de Treino
  const [methods, setMethods] = useState([]);
  const [showMethodForm, setShowMethodForm] = useState(false);
  const [editingMethod, setEditingMethod] = useState(null);
  const [methodForm, setMethodForm] = useState({ name: '', description: '' });

  // Estados para Super Séries
  const [superSeries, setSuperSeries] = useState([]);
  const [showSeriesForm, setShowSeriesForm] = useState(false);
  const [editingSeries, setEditingSeries] = useState(null);
  const [seriesForm, setSeriesForm] = useState({
    name: '',
    method_id: '',
    exercises: [],
    rest_between_exercises: '',
    rest_after_series: '',
    notes: ''
  });

  // Estados para biblioteca de exercícios
  const [exercisesLibrary, setExercisesLibrary] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showExerciseSelector, setShowExerciseSelector] = useState(false);

  // Estado para acordeão
  const [expandedSection, setExpandedSection] = useState('series'); // 'methods' ou 'series'

  useEffect(() => {
    fetchMethods();
    fetchSuperSeries();
    fetchExercisesLibrary();
  }, []);

  const fetchMethods = async () => {
    try {
      const res = await axios.get(`${API}/admin/training-methods`);
      setMethods(res.data);
    } catch (error) {
      console.error('Erro ao buscar métodos:', error);
    }
  };

  const fetchSuperSeries = async () => {
    try {
      const res = await axios.get(`${API}/admin/super-series`);
      setSuperSeries(res.data);
    } catch (error) {
      console.error('Erro ao buscar super séries:', error);
    }
  };

  const fetchExercisesLibrary = async () => {
    try {
      const res = await axios.get(`${API}/admin/exercises`);
      setExercisesLibrary(res.data);
    } catch (error) {
      console.error('Erro ao buscar exercícios:', error);
    }
  };

  // ==================== MÉTODOS ====================

  const handleSaveMethod = async () => {
    if (!methodForm.name.trim()) {
      toast.error('Nome do método é obrigatório');
      return;
    }

    try {
      if (editingMethod) {
        await axios.put(`${API}/admin/training-methods/${editingMethod.id}`, methodForm);
        toast.success('Método atualizado!');
      } else {
        await axios.post(`${API}/admin/training-methods`, methodForm);
        toast.success('Método criado!');
      }
      fetchMethods();
      setShowMethodForm(false);
      setEditingMethod(null);
      setMethodForm({ name: '', description: '' });
    } catch (error) {
      toast.error('Erro ao salvar método');
    }
  };

  const handleEditMethod = (method) => {
    setEditingMethod(method);
    setMethodForm({ name: method.name, description: method.description || '' });
    setShowMethodForm(true);
  };

  const handleDeleteMethod = async (method) => {
    if (!window.confirm(`Deletar método "${method.name}"?`)) return;
    try {
      await axios.delete(`${API}/admin/training-methods/${method.id}`);
      toast.success('Método deletado!');
      fetchMethods();
    } catch (error) {
      toast.error('Erro ao deletar método');
    }
  };

  // ==================== SUPER SÉRIES ====================

  const handleSaveSeries = async () => {
    if (!seriesForm.name.trim()) {
      toast.error('Nome da super série é obrigatório');
      return;
    }
    if (!seriesForm.method_id) {
      toast.error('Selecione um método de treino');
      return;
    }
    if (seriesForm.exercises.length < 2) {
      toast.error('Adicione pelo menos 2 exercícios');
      return;
    }

    try {
      if (editingSeries) {
        await axios.put(`${API}/admin/super-series/${editingSeries.id}`, seriesForm);
        toast.success('Super série atualizada!');
      } else {
        await axios.post(`${API}/admin/super-series`, seriesForm);
        toast.success('Super série criada!');
      }
      fetchSuperSeries();
      setShowSeriesForm(false);
      setEditingSeries(null);
      setSeriesForm({
        name: '',
        method_id: '',
        exercises: [],
        rest_between_exercises: '',
        rest_after_series: '',
        notes: ''
      });
    } catch (error) {
      toast.error('Erro ao salvar super série');
    }
  };

  const handleEditSeries = (series) => {
    setEditingSeries(series);
    setSeriesForm({
      name: series.name,
      method_id: series.method_id,
      exercises: series.exercises || [],
      rest_between_exercises: series.rest_between_exercises || '',
      rest_after_series: series.rest_after_series || '',
      notes: series.notes || ''
    });
    setShowSeriesForm(true);
  };

  const handleDeleteSeries = async (series) => {
    if (!window.confirm(`Deletar super série "${series.name}"?`)) return;
    try {
      await axios.delete(`${API}/admin/super-series/${series.id}`);
      toast.success('Super série deletada!');
      fetchSuperSeries();
    } catch (error) {
      toast.error('Erro ao deletar super série');
    }
  };

  const addExerciseToSeries = (exercise) => {
    const newExercise = {
      id: exercise.id || `temp-${Date.now()}`,
      name: exercise.name,
      youtube_url: exercise.youtube_url || '',
      instructions: exercise.instructions || '',
      execution_details: '',
      order: seriesForm.exercises.length
    };
    setSeriesForm({
      ...seriesForm,
      exercises: [...seriesForm.exercises, newExercise]
    });
    setShowExerciseSelector(false);
    setSearchTerm('');
  };

  const removeExerciseFromSeries = (index) => {
    const newExercises = seriesForm.exercises.filter((_, i) => i !== index);
    setSeriesForm({ ...seriesForm, exercises: newExercises });
  };

  const updateExerciseInSeries = (index, field, value) => {
    const newExercises = [...seriesForm.exercises];
    newExercises[index] = { ...newExercises[index], [field]: value };
    setSeriesForm({ ...seriesForm, exercises: newExercises });
  };

  const moveExercise = (index, direction) => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= seriesForm.exercises.length) return;
    
    const newExercises = [...seriesForm.exercises];
    [newExercises[index], newExercises[newIndex]] = [newExercises[newIndex], newExercises[index]];
    setSeriesForm({ ...seriesForm, exercises: newExercises });
  };

  const filteredExercises = exercisesLibrary.filter(ex =>
    ex.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6">
      {/* ==================== SEÇÃO SUPER SÉRIES ==================== */}
      <div className="bg-zinc-900/50 border border-white/10 rounded-2xl overflow-hidden">
        <button
          onClick={() => setExpandedSection(expandedSection === 'series' ? '' : 'series')}
          className="w-full p-4 flex items-center justify-between hover:bg-zinc-800/50 transition-colors"
        >
          <div className="flex items-center gap-3">
            <Layers className="w-6 h-6 text-neon-purple" />
            <div className="text-left">
              <h3 className="font-bold text-white text-lg">Super Séries</h3>
              <p className="text-zinc-500 text-sm">{superSeries.length} combinações salvas</p>
            </div>
          </div>
          {expandedSection === 'series' ? (
            <ChevronUp className="w-5 h-5 text-zinc-400" />
          ) : (
            <ChevronDown className="w-5 h-5 text-zinc-400" />
          )}
        </button>

        <AnimatePresence>
          {expandedSection === 'series' && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="border-t border-white/10"
            >
              <div className="p-4">
                {/* Botão Criar Super Série */}
                {!showSeriesForm && (
                  <Button
                    onClick={() => {
                      setShowSeriesForm(true);
                      setEditingSeries(null);
                      setSeriesForm({
                        name: '',
                        method_id: '',
                        exercises: [],
                        rest_between_exercises: '',
                        rest_after_series: '',
                        notes: ''
                      });
                    }}
                    className="w-full bg-neon-purple/20 border border-neon-purple/50 text-neon-purple hover:bg-neon-purple/30 mb-4"
                  >
                    <Plus className="w-5 h-5 mr-2" />
                    Criar Super Série
                  </Button>
                )}

                {/* Formulário de Super Série */}
                {showSeriesForm && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-zinc-950/50 border border-neon-purple/30 rounded-xl p-4 mb-4 space-y-4"
                  >
                    <h4 className="font-bold text-white">
                      {editingSeries ? 'Editar Super Série' : 'Nova Super Série'}
                    </h4>

                    {/* Nome */}
                    <Input
                      value={seriesForm.name}
                      onChange={(e) => setSeriesForm({ ...seriesForm, name: e.target.value })}
                      placeholder="Nome da super série (ex: Bi-Set Peito)"
                      className="bg-zinc-900 border-zinc-700 text-white"
                    />

                    {/* Método de Treino */}
                    <div>
                      <label className="block text-sm text-zinc-400 mb-2">Método de Treino</label>
                      <select
                        value={seriesForm.method_id}
                        onChange={(e) => setSeriesForm({ ...seriesForm, method_id: e.target.value })}
                        className="w-full h-10 bg-zinc-900 border border-zinc-700 rounded-lg text-white px-3"
                      >
                        <option value="">Selecione um método</option>
                        {methods.map(m => (
                          <option key={m.id} value={m.id}>{m.name}</option>
                        ))}
                      </select>
                    </div>

                    {/* Exercícios */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className="text-sm text-zinc-400">Exercícios ({seriesForm.exercises.length})</label>
                        <Button
                          onClick={() => setShowExerciseSelector(true)}
                          size="sm"
                          className="bg-neon-lime/20 text-neon-lime hover:bg-neon-lime/30"
                        >
                          <Plus className="w-4 h-4 mr-1" />
                          Adicionar
                        </Button>
                      </div>

                      {/* Seletor de Exercícios */}
                      {showExerciseSelector && (
                        <div className="bg-zinc-900 border border-zinc-700 rounded-lg p-3 mb-3">
                          <div className="relative mb-2">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                            <Input
                              value={searchTerm}
                              onChange={(e) => setSearchTerm(e.target.value)}
                              placeholder="Buscar exercício..."
                              className="pl-9 bg-zinc-800 border-zinc-600 text-white h-9"
                            />
                          </div>
                          <div className="max-h-40 overflow-y-auto space-y-1">
                            {filteredExercises.map(ex => (
                              <button
                                key={ex.id}
                                onClick={() => addExerciseToSeries(ex)}
                                className="w-full text-left p-2 hover:bg-zinc-800 rounded-lg text-sm text-white"
                              >
                                {ex.name}
                              </button>
                            ))}
                          </div>
                          <Button
                            onClick={() => { setShowExerciseSelector(false); setSearchTerm(''); }}
                            size="sm"
                            className="w-full mt-2 bg-zinc-800"
                          >
                            Fechar
                          </Button>
                        </div>
                      )}

                      {/* Lista de Exercícios Adicionados */}
                      <div className="space-y-2">
                        {seriesForm.exercises.map((ex, idx) => (
                          <div key={ex.id || idx} className="bg-zinc-900 border border-zinc-700 rounded-lg p-3">
                            <div className="flex items-center gap-2 mb-2">
                              <div className="flex flex-col gap-1">
                                <button
                                  onClick={() => moveExercise(idx, 'up')}
                                  disabled={idx === 0}
                                  className="p-1 hover:bg-zinc-800 rounded disabled:opacity-30"
                                >
                                  <ChevronUp className="w-3 h-3 text-zinc-400" />
                                </button>
                                <button
                                  onClick={() => moveExercise(idx, 'down')}
                                  disabled={idx === seriesForm.exercises.length - 1}
                                  className="p-1 hover:bg-zinc-800 rounded disabled:opacity-30"
                                >
                                  <ChevronDown className="w-3 h-3 text-zinc-400" />
                                </button>
                              </div>
                              <span className="text-neon-lime font-bold text-sm">{idx + 1}.</span>
                              <span className="flex-1 text-white font-medium truncate">{ex.name}</span>
                              <button
                                onClick={() => removeExerciseFromSeries(idx)}
                                className="p-1 hover:bg-red-500/20 rounded text-red-500"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                            <Input
                              value={ex.execution_details || ''}
                              onChange={(e) => updateExerciseInSeries(idx, 'execution_details', e.target.value)}
                              placeholder="Execução (ex: 12 reps)"
                              className="bg-zinc-800 border-zinc-600 text-white h-8 text-sm"
                            />
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Descanso */}
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs text-zinc-400 mb-1">Descanso entre exercícios</label>
                        <Input
                          value={seriesForm.rest_between_exercises}
                          onChange={(e) => setSeriesForm({ ...seriesForm, rest_between_exercises: e.target.value })}
                          placeholder="Ex: sem descanso"
                          className="bg-zinc-900 border-zinc-700 text-white h-9 text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-zinc-400 mb-1">Descanso após série</label>
                        <Input
                          value={seriesForm.rest_after_series}
                          onChange={(e) => setSeriesForm({ ...seriesForm, rest_after_series: e.target.value })}
                          placeholder="Ex: 60-90 seg"
                          className="bg-zinc-900 border-zinc-700 text-white h-9 text-sm"
                        />
                      </div>
                    </div>

                    {/* Notas */}
                    <textarea
                      value={seriesForm.notes}
                      onChange={(e) => setSeriesForm({ ...seriesForm, notes: e.target.value })}
                      placeholder="Observações (opcional)"
                      className="w-full bg-zinc-900 border border-zinc-700 text-white rounded-lg p-3 min-h-[60px] text-sm"
                    />

                    {/* Botões */}
                    <div className="flex gap-2">
                      <Button onClick={handleSaveSeries} className="flex-1 bg-neon-purple text-white">
                        <Save className="w-4 h-4 mr-2" />
                        {editingSeries ? 'Atualizar' : 'Salvar'}
                      </Button>
                      <Button
                        onClick={() => { setShowSeriesForm(false); setEditingSeries(null); }}
                        className="bg-zinc-800"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </motion.div>
                )}

                {/* Lista de Super Séries */}
                <div className="space-y-3">
                  {superSeries.map(series => (
                    <div
                      key={series.id}
                      className="bg-zinc-950/50 border border-white/5 rounded-xl p-4"
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <h5 className="font-bold text-white">{series.name}</h5>
                          <span className="inline-block px-2 py-0.5 bg-neon-purple/20 text-neon-purple text-xs font-bold rounded-full mt-1">
                            {series.method_name}
                          </span>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            onClick={() => handleEditSeries(series)}
                            variant="ghost"
                            size="sm"
                            className="text-zinc-400 hover:text-neon-lime"
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button
                            onClick={() => handleDeleteSeries(series)}
                            variant="ghost"
                            size="sm"
                            className="text-zinc-400 hover:text-red-500"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                      <div className="text-sm text-zinc-400 space-y-1">
                        {series.exercises?.map((ex, i) => (
                          <div key={i} className="flex items-center gap-2">
                            <span className="text-neon-lime">{i + 1}.</span>
                            <span>{ex.name}</span>
                            {ex.execution_details && (
                              <span className="text-zinc-500">- {ex.execution_details}</span>
                            )}
                          </div>
                        ))}
                      </div>
                      {(series.rest_between_exercises || series.rest_after_series) && (
                        <div className="mt-2 flex gap-4 text-xs text-zinc-500">
                          {series.rest_between_exercises && (
                            <span>Entre exercícios: {series.rest_between_exercises}</span>
                          )}
                          {series.rest_after_series && (
                            <span>Após série: {series.rest_after_series}</span>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                  {superSeries.length === 0 && !showSeriesForm && (
                    <p className="text-center text-zinc-500 py-4">
                      Nenhuma super série criada ainda
                    </p>
                  )}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* ==================== SEÇÃO MÉTODOS DE TREINO ==================== */}
      <div className="bg-zinc-900/50 border border-white/10 rounded-2xl overflow-hidden">
        <button
          onClick={() => setExpandedSection(expandedSection === 'methods' ? '' : 'methods')}
          className="w-full p-4 flex items-center justify-between hover:bg-zinc-800/50 transition-colors"
        >
          <div className="flex items-center gap-3">
            <Dumbbell className="w-6 h-6 text-neon-lime" />
            <div className="text-left">
              <h3 className="font-bold text-white text-lg">Métodos de Treino</h3>
              <p className="text-zinc-500 text-sm">{methods.length} métodos cadastrados</p>
            </div>
          </div>
          {expandedSection === 'methods' ? (
            <ChevronUp className="w-5 h-5 text-zinc-400" />
          ) : (
            <ChevronDown className="w-5 h-5 text-zinc-400" />
          )}
        </button>

        <AnimatePresence>
          {expandedSection === 'methods' && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="border-t border-white/10"
            >
              <div className="p-4">
                {/* Botão Criar Método */}
                {!showMethodForm && (
                  <Button
                    onClick={() => {
                      setShowMethodForm(true);
                      setEditingMethod(null);
                      setMethodForm({ name: '', description: '' });
                    }}
                    className="w-full bg-neon-lime/20 border border-neon-lime/50 text-neon-lime hover:bg-neon-lime/30 mb-4"
                  >
                    <Plus className="w-5 h-5 mr-2" />
                    Novo Método
                  </Button>
                )}

                {/* Formulário de Método */}
                {showMethodForm && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-zinc-950/50 border border-neon-lime/30 rounded-xl p-4 mb-4 space-y-3"
                  >
                    <Input
                      value={methodForm.name}
                      onChange={(e) => setMethodForm({ ...methodForm, name: e.target.value })}
                      placeholder="Nome do método (ex: Bi-Set)"
                      className="bg-zinc-900 border-zinc-700 text-white"
                    />
                    <Input
                      value={methodForm.description}
                      onChange={(e) => setMethodForm({ ...methodForm, description: e.target.value })}
                      placeholder="Descrição (opcional)"
                      className="bg-zinc-900 border-zinc-700 text-white"
                    />
                    <div className="flex gap-2">
                      <Button onClick={handleSaveMethod} className="flex-1 bg-neon-lime text-black font-bold">
                        <Save className="w-4 h-4 mr-2" />
                        {editingMethod ? 'Atualizar' : 'Salvar'}
                      </Button>
                      <Button
                        onClick={() => { setShowMethodForm(false); setEditingMethod(null); }}
                        className="bg-zinc-800"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </motion.div>
                )}

                {/* Lista de Métodos */}
                <div className="space-y-2">
                  {methods.map(method => (
                    <div
                      key={method.id}
                      className="flex items-center justify-between bg-zinc-950/50 border border-white/5 rounded-lg p-3"
                    >
                      <div>
                        <h5 className="font-bold text-white">{method.name}</h5>
                        {method.description && (
                          <p className="text-zinc-500 text-sm">{method.description}</p>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <Button
                          onClick={() => handleEditMethod(method)}
                          variant="ghost"
                          size="sm"
                          className="text-zinc-400 hover:text-neon-lime"
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button
                          onClick={() => handleDeleteMethod(method)}
                          variant="ghost"
                          size="sm"
                          className="text-zinc-400 hover:text-red-500"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                  {methods.length === 0 && !showMethodForm && (
                    <p className="text-center text-zinc-500 py-4">
                      Nenhum método cadastrado ainda
                    </p>
                  )}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default SuperSeriesManager;
